import { useEffect } from "react";
import { useLocation } from "@tanstack/react-router";

export interface Props {
  darkLogoImage: string;
  lightLogoImage: string;
}

const SCROLL_LIMIT = 100;
const WIDTH_BREAKPOINT = 1024;

const setElementAsRelative = () => {
  const header = document.querySelector(".header");
  header?.classList.add("home-relative");

  globalThis.addEventListener("scroll", () => {
    if (globalThis.scrollY > SCROLL_LIMIT) {
      header?.classList.remove("home-relative");
    } else if (globalThis.scrollY < SCROLL_LIMIT) {
      header?.classList.add("home-relative");
    }
  });
};

const setElementsAsScrolled = (darkImage: string) => {
  const header = document.querySelector(".header");
  const logos = header?.querySelectorAll(".logoImage");
  const navMenu = header?.querySelector(".header-navmenu");
  const searchbarInput = header?.querySelector(".minisearchbar-input");
  const searchbarForm = header?.querySelector(".minisearchbar-form");
  const b2bButton = header?.querySelector(".b2b-button");
  const loginIcon = header?.querySelector(".login-icon");
  const cartIcons = header?.querySelectorAll(".cart-icon");

  header && header.classList.add("scrolled");
  navMenu && navMenu.classList.add("scrolled");
  searchbarInput && searchbarInput.classList.add("scrolled");
  searchbarForm && searchbarForm.classList.add("scrolled");
  b2bButton && b2bButton.classList.add("scrolled");

  if (logos) {
    logos.forEach((logo) => ((logo as HTMLImageElement).srcset = darkImage));
  }

  if (loginIcon) {
    const imgUrl = "/icon_my-account-gray-new.png";
    (loginIcon as HTMLImageElement).srcset = imgUrl;
  }

  if (cartIcons) {
    const imgUrl = (
      globalThis.innerWidth < WIDTH_BREAKPOINT
        ? "/icon_shopping-bag-gray-mobile.png"
        : "/icon_shopping-bag-gray.png"
    );
    cartIcons.forEach(
      (cartIcon) => ((cartIcon as HTMLImageElement).srcset = imgUrl)
    );
  }
};

const setElementsAsUnscrolled = (lightImage: string) => {
  const header = document.querySelector(".header");
  const logos = header?.querySelectorAll(".logoImage");
  const navMenu = header?.querySelector(".header-navmenu");
  const searchbarInput = header?.querySelector(".minisearchbar-input");
  const searchbarForm = header?.querySelector(".minisearchbar-form");
  const b2bButton = header?.querySelector(".b2b-button");
  const loginIcon = header?.querySelector(".login-icon");
  const cartIcons = header?.querySelectorAll(".cart-icon");

  header && header.classList.remove("scrolled");
  navMenu && navMenu.classList.remove("scrolled");
  searchbarInput && searchbarInput.classList.remove("scrolled");
  searchbarForm && searchbarForm.classList.remove("scrolled");
  b2bButton && b2bButton.classList.remove("scrolled");

  if (logos) {
    logos.forEach((logo) => ((logo as HTMLImageElement).srcset = lightImage));
  }

  if (loginIcon) {
    const imgUrl = "/icon_my-account-new-white.png";
    (loginIcon as HTMLImageElement).srcset = imgUrl;
  }

  if (cartIcons) {
    const imgUrl = "/icon_shopping-bag-white.png";
    cartIcons.forEach(
      (cartIcon) => ((cartIcon as HTMLImageElement).srcset = imgUrl)
    );
  }
};

const HOME_PATHS = new Set(["/", "/vivame", "/black-friday", "/natal"]);

function HeaderJS({ darkLogoImage, lightLogoImage }: Props) {
  const pathname = useLocation({ select: (location) => location.pathname });
  const isHome = HOME_PATHS.has(pathname);

  useEffect(() => {
    if (!isHome) {
      setElementsAsScrolled(darkLogoImage);
      setElementAsRelative();
      const headerBackground = document.querySelector(".header-background");
      if (headerBackground) {
        headerBackground.classList.add("header-hidden");
      }
      return;
    }

    const handleMouseOver = () => {
      setElementsAsScrolled(darkLogoImage);
    };

    const handleMouseLeave = () => {
      if (
        globalThis.scrollY > SCROLL_LIMIT ||
        globalThis.innerWidth < WIDTH_BREAKPOINT
      ) {
        return;
      }

      setElementsAsUnscrolled(lightLogoImage);
    };

    const handleScroll = () => {
      if (
        globalThis.scrollY > SCROLL_LIMIT ||
        globalThis.innerWidth < WIDTH_BREAKPOINT
      ) {
        setElementsAsScrolled(darkLogoImage);
      } else {
        setElementsAsUnscrolled(lightLogoImage);
      }
    };

    const handleResize = () => {
      if (globalThis.innerWidth < WIDTH_BREAKPOINT) {
        setElementsAsScrolled(darkLogoImage);
      }
    };

    const header = document.querySelector(".header");

    if (header) {
      header.addEventListener("mouseover", handleMouseOver);
      header.addEventListener("mouseleave", handleMouseLeave);
      globalThis.addEventListener("scroll", handleScroll);
      globalThis.addEventListener("resize", handleResize);
    }

    handleResize();

    return () => {
      if (header) {
        header.removeEventListener("mouseover", handleMouseOver);
        header.removeEventListener("mouseleave", handleMouseLeave);
        globalThis.removeEventListener("scroll", handleScroll);
        globalThis.removeEventListener("resize", handleResize);
      }
    };
  }, [isHome, darkLogoImage, lightLogoImage]);

  return <div data-header-controller-js />;
}

export default HeaderJS;
